import { TokenManager } from './auth/tokenManager.js';
import { CircuitBreaker } from './simulation/circuitBreaker.js';
import { RestClient } from './rest/client.js';
import { DossiersClientImpl } from './rest/dossiers.js';
import { AdminClientImpl } from './rest/admin.js';
import { FinopsClientImpl } from './rest/finops.js';
import { BeneficiaryClientImpl, RuleEngineClientImpl } from './rest/demo_clause.js';
import { TotpClientImpl } from './rest/totp.js';
import { RagClientImpl } from './rest/rag.js';
import { HitlClientImpl } from './rest/hitl.js';
import { AiActService } from './rest/aiAct.js';
import { AuditClientStub, SessionsClientStub, SuspensionsClientStub, ChatClientStub, } from './rest/stubs.js';
import { handleResponse } from './client.js';
export class AssistiaSDK {
    baseUrl;
    simulationProvider;
    timeoutMs;
    // ── Sous-modules ──────────────────────────────────────────────────────────
    /** Gestion JWT — implémentation Gemini (S5.4-06) */
    tokenManager;
    /** Circuit breaker — implémentation Gemini (S5.4-07) */
    circuitBreaker;
    /** Client HTTP centralisé (auth + circuit breaker + retry) */
    http;
    /** Endpoints dossiers */
    dossiers;
    /** Endpoints admin (users, health) */
    admin;
    /** Endpoints finops / premium */
    finops;
    /** Endpoints bénéficiaire */
    beneficiary;
    /** Endpoints rule engine */
    ruleEngine;
    /** Endpoints HITL */
    hitl;
    /** Endpoints AI Act Register */
    aiAct;
    /**
     * Endpoints TOTP — setup, activation, vérification, désactivation.
     * @see docs/backend/06-API.md §1.1
     */
    totp;
    /**
     * Endpoints RAG — indexation et requête de dossiers.
     * @see docs/backend/06-API.md §12
     */
    rag;
    /** Audit — stub (planned: Sprint OPS-02) */
    audit;
    /** Sessions — stub (planned: Sprint P1) */
    sessions;
    /** Suspensions — stub (planned: Sprint HITL-02) */
    suspensions;
    /** Chat real-time — stub (planned: Sprint P1) */
    chat;
    constructor(options) {
        this.baseUrl = options.baseUrl.replace(/\/$/, '');
        this.simulationProvider = options.simulationProvider;
        this.timeoutMs = options.timeoutMs ?? 10_000;
        // Auth + Circuit Breaker — implémentations Gemini (S5.4-06/07)
        this.tokenManager = new TokenManager();
        this.circuitBreaker = new CircuitBreaker({
            failureThreshold: 3,
            openTimeout: 30_000,
            requestTimeout: this.timeoutMs,
            demoMode: options.demoMode ?? false,
        });
        // Passer simulationProvider uniquement si défini (exactOptionalPropertyTypes)
        const sim = this.simulationProvider;
        // Client HTTP de base
        this.http = new RestClient({
            baseUrl: this.baseUrl,
            tokenManager: this.tokenManager,
            circuitBreaker: this.circuitBreaker,
            timeoutMs: this.timeoutMs,
            ...(sim !== undefined && { simulationProvider: sim }),
        });
        // Clients REST
        this.dossiers = new DossiersClientImpl(this.http, sim);
        this.admin = new AdminClientImpl(this.http, sim);
        this.finops = new FinopsClientImpl(this.http, sim);
        this.beneficiary = new BeneficiaryClientImpl(this.http, sim);
        this.ruleEngine = new RuleEngineClientImpl(this.http, sim);
        this.hitl = new HitlClientImpl(this.http, sim);
        this.aiAct = new AiActService(this.http);
        this.totp = new TotpClientImpl(this.http);
        this.rag = new RagClientImpl(this.http);
        // Stub clients (future sprints)
        this.audit = new AuditClientStub(this.http);
        this.sessions = new SessionsClientStub(this.http);
        this.suspensions = new SuspensionsClientStub(this.http);
        this.chat = new ChatClientStub(this.baseUrl);
    }
    /**
     * Initialise le SDK avec des options globales, supportant aussi la
     * configuration facultative de la persistance du chat (7.3-09).
     *
     * `storage` / `dbName` sont des indications typées pour le ChatStore de
     * l'application (voir README, « Migration Guide 7.3 ») — le SDK ne crée
     * pas lui-même le stockage tant que le backend `'server'` (P1) n'est pas livré.
     */
    static init(options) {
        return new AssistiaSDK(options);
    }
    /**
     * Send a chat message to the backend, returning a structured SecurityResponse.
     *
     * 6.8-03 corrections :
     * - Le succès est un flux SSE (HTTP 200) — il n'est JAMAIS parsé en JSON :
     *   `data` porte la Response brute, à consommer en streaming par l'appelant.
     * - Un 403 guardrail ({detail, blocked: true} — backend 6.8-01) est mappé en
     *   SecurityErrorType.BLOCKED ; un 403 auth/licence reste FORBIDDEN.
     * - Chemin par défaut `/chat/stream` (route framework), surchargeable.
     */
    async sendMessage(message, options = {}) {
        const path = options.path ?? '/chat/stream';
        const resp = await fetch(`${this.baseUrl}${path}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                message,
                ...(options.channelId ? { channel_id: options.channelId } : {}),
                ...(options.sessionId ? { session_id: options.sessionId } : {}),
            }),
        });
        if (resp.ok) {
            return { blocked: false, status: resp.status, data: resp };
        }
        // Non-OK : mapping structuré (BLOCKED / FORBIDDEN / INVALID_PAYLOAD)
        return handleResponse(resp);
    }
}
//# sourceMappingURL=sdk.js.map